import theme, {
  teceoBlueBackground,
  teceoGrayPrimary,
  teceoGreen,
  teceoPrimaryMain,
  tertiaryMain,
} from '../Theme';
import LangEnum from '../lang/LangEnum';
import { Option } from '../lib/OptionsLib';
import {
  AccountsReceivableDocumentTypeEnum,
  InstallmentStatusEnum,
} from '../modules/accountsReceivable/types/accountsReceivable.types';
import {
  CustomerOriginType,
  CustomerSynchronizedStatus,
} from '../modules/customer/types/customer.enum';
import { CustomerStatusType } from '../modules/customer/types/customerStatusType';
import { InvoiceStatusType } from '../modules/invoices/types/invoiceStatusType';

type Enumerated = {
  values: Readonly<Option[]>;
};

export type AveragePriceOption = Option & {
  lowerValue: number;
  biggerValue?: number;
};

export type EnumeratedAveragePrices = {
  values: Readonly<AveragePriceOption[]>;
};

export type EnumeratedWithConfig = {
  translation: Readonly<Record<string, string>>;
  data: Readonly<Record<string, any>>;
} & Enumerated;

const indexByValue = (enumObject: Enumerated, field: keyof Option) => {
  const enumConfig: Record<string, string> = {};
  for (let index = 0; index < enumObject.values.length; index += 1) {
    const option = enumObject.values[index];
    enumConfig[option.value] = option[field];
  }
  return enumConfig;
};

const valuesToData = (enumObject: Enumerated): Record<string, string> =>
  indexByValue(enumObject, 'data');

const valuesToConfig = (enumObject: Enumerated): Record<string, string> =>
  indexByValue(enumObject, 'label');

const OutOfStock: Enumerated = {
  values: [
    { label: 'Ocultar', value: 'HIDE' },
    { label: 'Esgotado', value: 'SHOW_UNAVAILABLE' },
    { label: 'Vender', value: 'CONTINUE_SELLING' },
  ],
};

const BannerType: Enumerated = {
  values: [
    { label: 'Produto', value: 'PRODUCT' },
    { label: 'Catálogo', value: 'CATALOG' },
  ],
};

export enum FreightType {
  CIF = 'CIF',
  FOB = 'FOB',
  HIRED_BY_THIRD_PARTY = 'HIRED_BY_THIRD_PARTY',
  NO_TRANSPORT_OCCURENCE = 'NO_TRANSPORT_OCCURENCE',
  SENDER_RESPONSABILITY = 'SENDER_RESPONSABILITY',
  RECEIVER_RESPONSABILITY = 'RECEIVER_RESPONSABILITY',
}

const FreightTypeEnumerated: Enumerated = {
  values: [
    { value: 'CIF', label: LangEnum.FREIGHT_TYPE_CIF },

    { value: 'FOB', label: LangEnum.FREIGHT_TYPE_FOB },

    {
      value: 'HIRED_BY_THIRD_PARTY',
      label: LangEnum.FREIGHT_TYPE_HIRED_BY_THIRD_PARTY,
    },

    {
      value: 'NO_TRANSPORT_OCCURENCE',
      label: LangEnum.FREIGHT_TYPE_NO_TRANSPORT_OCCURENCE,
    },

    {
      value: 'SENDER_RESPONSABILITY',
      label: LangEnum.FREIGHT_TYPE_RECEIVER_RESPONSABILITY,
    },

    {
      value: 'RECEIVER_RESPONSABILITY',
      label: LangEnum.FREIGHT_TYPE_SENDER_RESPONSABILITY,
    },
  ],
};

const CustomerStatus: Enumerated = {
  values: [
    {
      value: CustomerStatusType.DRAFT,
      label: LangEnum.CUSTOMER_STATUS_DRAFT,
      data: { color: theme.palette.grey[400] },
    },
    {
      value: CustomerStatusType.PENDING,
      label: LangEnum.CUSTOMER_STATUS_PENDING,
      data: { color: teceoBlueBackground },
    },
    {
      value: CustomerStatusType.APPROVED,
      label: LangEnum.CUSTOMER_STATUS_APPROVED,
      data: { color: tertiaryMain },
    },
    {
      value: CustomerStatusType.INACTIVE,
      label: LangEnum.CUSTOMER_STATUS_INACTIVE,
      data: { color: teceoGrayPrimary },
    },
    {
      value: CustomerStatusType.BLOCKED,
      label: LangEnum.CUSTOMER_STATUS_BLOCKED,
      data: { color: theme.palette.grey[700] },
    },
  ],
};

const CustomerSynchronized: Enumerated = {
  values: [
    {
      value: CustomerSynchronizedStatus.SUCCESS,
      label: LangEnum.GLOBAL_SUCCESS,
      data: { color: teceoBlueBackground },
    },
    {
      value: CustomerSynchronizedStatus.NOT_SYNCHRONIZED,
      label: LangEnum.GLOBAL_NOT_INTEGRATED,
      data: { color: teceoGreen },
    },
    {
      value: CustomerSynchronizedStatus.ERROR,
      label: LangEnum.GLOBAL_ERROR,
      data: { color: 'red' },
    },
    {
      value: CustomerSynchronizedStatus.PROCESSING,
      label: LangEnum.GLOBAL_PROCESSING,
      data: { color: teceoBlueBackground },
    },
  ],
};

const CustomerOrigin: Enumerated = {
  values: [
    { value: CustomerOriginType.INTEGRATION, label: LangEnum.GLOBAL_INTEGRATION },
    { value: CustomerOriginType.CONNECTION, label: LangEnum.GLOBAL_TECEO },
    { value: CustomerOriginType.MANUAL, label: LangEnum.GLOBAL_MANUAL_REGISTRATION },
    { value: CustomerOriginType.SPREADSHEET_IMPORT, label: LangEnum.GLOBAL_SPREADSHEET_IMPORT },
    { value: CustomerOriginType.EXTERNAL_FORM, label: LangEnum.GLOBAL_EXTERNAL_FORM },
  ],
};

const CustomerStatusSynchronized: Enumerated = {
  values: [
    {
      value: CustomerStatusType.PENDING,
      label: LangEnum.CUSTOMER_STATUS_PENDING,
      data: { color: teceoBlueBackground },
    },
    {
      value: CustomerStatusType.APPROVED,
      label: LangEnum.CUSTOMER_STATUS_APPROVED,
      data: { color: teceoGreen },
    },
  ],
};

const MyNetworkType: Enumerated = {
  values: [
    { value: 'CONVIDAR', label: LangEnum.GLOBAL_NO_ACCESS_TO_TECEO },
    { value: 'CONECTAR', label: LangEnum.GLOBAL_NOT_CONNECTED },
    { value: 'CONVITE_ENVIADO', label: LangEnum.MY_NETWORK_STATUS_INVITED },
    { value: 'CONECTADO', label: LangEnum.MY_NETWORK_STATUS_CONNECTED },
    { value: 'CONVITE_RECEBIDO', label: LangEnum.MY_NETWORK_STATUS_INVITE_RECEIVED },
  ],
};

const EventExploreStatus: Enumerated = {
  values: [
    { value: 'CONECTAR', label: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_CONNECT },
    { value: 'CONECTADO', label: LangEnum.MY_NETWORK_EXPLORE_CONNECTED },
    { value: 'SOLICITADO', label: LangEnum.MY_NETWORK_EXPLORE_REQUESTED },
  ],
};

const WholesaleMediumPricesType: EnumeratedAveragePrices = {
  values: [
    { value: 'LOWER_THAN_100', label: LangEnum.PRICES_UNDER, lowerValue: 100 },
    {
      value: 'FROM_101_TO_200',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 101,
      biggerValue: 200,
    },
    {
      value: 'FROM_201_TO_400',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 201,
      biggerValue: 400,
    },
    {
      value: 'FROM_401_TO_600',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 401,
      biggerValue: 600,
    },
    { value: 'ABOVE_600', label: LangEnum.PRICES_ABOVE, lowerValue: 600 },
  ],
};

const RetailMediumPricesType: EnumeratedAveragePrices = {
  values: [
    { value: 'LOWER_THAN_200', label: LangEnum.PRICES_UNDER, lowerValue: 200 },
    {
      value: 'FROM_201_TO_400',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 201,
      biggerValue: 400,
    },
    {
      value: 'FROM_401_TO_800',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 401,
      biggerValue: 800,
    },
    {
      value: 'FROM_801_TO_1200',
      label: LangEnum.PRICES_FROM_TO,
      lowerValue: 801,
      biggerValue: 1200,
    },
    { value: 'ABOVE_1200', label: LangEnum.PRICES_ABOVE, lowerValue: 1200 },
  ],
};

const AccountsReceivableInstallmentStatus: Enumerated = {
  values: [
    {
      value: InstallmentStatusEnum.PAID,
      label: LangEnum.GLOBAL_RECEIVED_FEMININE,
      data: { color: tertiaryMain },
    },
    {
      value: InstallmentStatusEnum.PENDING,
      label: LangEnum.GLOBAL_DUE,
      data: { color: teceoPrimaryMain },
    },
    {
      value: InstallmentStatusEnum.OVERDUE,
      label: LangEnum.ACCOUNTS_RECEIVABLE_OVERDUE,
      data: { color: teceoGrayPrimary },
    },
  ],
};

const AccountsPayableInstallmentStatus: Enumerated = {
  values: [
    {
      value: InstallmentStatusEnum.PAID,
      label: LangEnum.GLOBAL_PAID_FEMININE,
      data: { color: tertiaryMain },
    },
    {
      value: InstallmentStatusEnum.PENDING,
      label: LangEnum.GLOBAL_DUE,
      data: { color: teceoPrimaryMain },
    },
    {
      value: InstallmentStatusEnum.OVERDUE,
      label: LangEnum.ACCOUNTS_RECEIVABLE_OVERDUE,
      data: { color: teceoGrayPrimary },
    },
  ],
};

export const AccountsReceivableDocumentType: Enumerated = {
  values: [
    { label: LangEnum.GLOBAL_PAYMENT_METHOD_CASH, value: AccountsReceivableDocumentTypeEnum.CASH },
    { label: LangEnum.GLOBAL_PAYMENT_METHOD_PIX, value: AccountsReceivableDocumentTypeEnum.PIX },
    {
      label: LangEnum.GLOBAL_PAYMENT_METHOD_CREDIT_CARD,
      value: AccountsReceivableDocumentTypeEnum.CREDIT,
    },
    {
      label: LangEnum.GLOBAL_PAYMENT_METHOD_DEBIT_CARD,
      value: AccountsReceivableDocumentTypeEnum.DEBIT,
    },
    {
      label: LangEnum.GLOBAL_PAYMENT_METHOD_CHECK,
      value: AccountsReceivableDocumentTypeEnum.CHECK,
    },
    {
      label: LangEnum.GLOBAL_PAYMENT_METHOD_INVOICE,
      value: AccountsReceivableDocumentTypeEnum.INVOICE,
    },
  ],
};

export const buildEnum = (enumObject: Enumerated): Readonly<EnumeratedWithConfig> => {
  const withConfig: EnumeratedWithConfig = {
    ...enumObject,
    translation: valuesToConfig(enumObject),
    data: valuesToData(enumObject),
  };
  const freezed = Object.freeze(withConfig);
  return freezed;
};

export const OutOfStockEnum = buildEnum(OutOfStock);
export const BannerTypeEnum = buildEnum(BannerType);
export const CustomerSynchronizedEnum = buildEnum(CustomerSynchronized);
export const CustomerStatusEnum = buildEnum(CustomerStatus);
export const CustomerOriginEnum = buildEnum(CustomerOrigin);
export const CustomerStatusSynchronizedEnum = buildEnum(CustomerStatusSynchronized);
export const MyNetworkTypeEnum = buildEnum(MyNetworkType);
export const EventExploreStatusEnum = buildEnum(EventExploreStatus);
export const WholesaleMediumPricesTypeEnum = buildEnum(WholesaleMediumPricesType);
export const RetailMediumPricesTypeEnum = buildEnum(RetailMediumPricesType);
export const FreightTypeEnum = buildEnum(FreightTypeEnumerated);
export const accountsReceivableInstallmentsStatus = buildEnum(AccountsReceivableInstallmentStatus);
export const accountsPayableInstallmentsStatus = buildEnum(AccountsPayableInstallmentStatus);
export const accountsReceivableDocumentTypeEnum = buildEnum(AccountsReceivableDocumentType);

const InvoiceStatus: Enumerated = {
  values: [
    {
      value: InvoiceStatusType.INVOICED,
      label: LangEnum.GLOBAL_INVOICED,
      data: { color: 'success' },
    },
    {
      value: InvoiceStatusType.CANCELED,
      label: LangEnum.GLOBAL_CANCELED,
      data: { color: 'error' },
    },
    {
      value: InvoiceStatusType.REJECTED,
      label: LangEnum.GLOBAL_REJECTED,
      data: { color: 'error' },
    },
    {
      value: InvoiceStatusType.DELETED,
      label: LangEnum.GLOBAL_DELETED,
      data: { color: 'error' },
    },
  ],
};

export const InvoiceStatusEnum = buildEnum(InvoiceStatus);
